/** @jsxImportSource theme-ui @jsxRuntime classic */
import { H } from '@hzdg/sectioning';
import slugify from 'slugify';
import React from 'react';
import { ThemeUIStyleObject } from 'theme-ui';

import { SanityRichText } from './SanityRichText';
import Picture from './Picture';

import { Box, Flex, Grid, Link, Text, IntrinsicImage } from '.';

import type { Block } from '@sanity/block-content-to-react';
import type { IGatsbyImageData } from 'gatsby-plugin-image';

export type CUSTOM_CALLOUT_CARD_TYPES =
  | 'HOLIDAY_CARD'
  | 'HOLIDAY_MR'
  | 'HOLIDAY_ML'
  | 'HOLIDAY_NARROW'
  | 'HOLIDAY_FULL_WIDTH_NARROW'
  | 'RINK_TOP'
  | 'RINK_BOTTOM'
  | 'RINK_LOUNGE'
  | 'LAST_CARD';

const getCustomCardStyles = (customCardType?: CUSTOM_CALLOUT_CARD_TYPES) => {
  switch (customCardType) {
    case 'HOLIDAY_CARD':
      return { mr: 'auto', ml: 'auto' };
    case 'HOLIDAY_MR':
      return { mr: [4, 0] };
    case 'HOLIDAY_ML':
      return { ml: [6] };
    case 'HOLIDAY_NARROW':
      return { ml: [7] };
    case 'HOLIDAY_FULL_WIDTH_NARROW':
      return {
        gridColumn: '1 / -1',
        mx: 'auto',
        mt: [4, null, null],
        mb: [3, null, 3],
      };
    case 'RINK_TOP':
      return { mr: [4, 6] };
    case 'RINK_BOTTOM':
      return { mr: [8], mb: [0, null, 0] };
    case 'RINK_LOUNGE':
      return { mr: [4, 6], ml: [0] };
    case 'LAST_CARD':
      return { height: ['auto', 'auto', 220] };
    default:
      return {};
  }
};

export interface CalloutGridCardProps {
  /** Grid card index
   * this informs if card is on right or left side
   *  */
  index: number;
  /** Grid card title */
  title: string;
  /** Grid card caption */
  caption?: string;
  align?: string;
  /** Grid card description */
  description: string | Block[];
  /** Grid card local image */
  imageSrc?: string;
  /** Grid card gatsby image */
  gatsbyImageData?: IGatsbyImageData;
  /** Grid card alt */
  alt: string;
  /** Grid card links */
  links?: readonly {
    url: string;
    label: string;
  }[];
  customCardType?: CUSTOM_CALLOUT_CARD_TYPES;
  maxWidth?: number | string;
  className?: string;
  ratio?: number;
  sx?: ThemeUIStyleObject;
}

export const CalloutGridCard = ({
  index,
  title,
  caption,
  description,
  alt,
  links,
  maxWidth = 520,
  customCardType,
  ...imageProps
}: CalloutGridCardProps): JSX.Element => {
  const evenCard = index % 2 === 0;

  return (
    <Box
      data-module="CalloutGridCard"
      id={slugify(title, { lower: true })}
      sx={{
        maxWidth,
        mb: [5, 7],
        mr: evenCard ? 5 : 0,
        ml: evenCard ? 0 : 5,
        height: 'fit-content',
        ...getCustomCardStyles(customCardType),
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {imageProps.imageSrc && (
          <Picture alt={alt} ratio={520 / 489} src={imageProps.imageSrc} />
        )}
        {imageProps.gatsbyImageData && (
          <IntrinsicImage alt={alt} image={imageProps.gatsbyImageData} />
        )}
        {caption && (
          <Text
            sx={{
              position: 'absolute',
              fontSize: [1, 2],
              bottom: [2, 3],
              right: [2, 3],
              color: 'mediaCaption',
              zIndex: 5,
            }}
          >
            {caption}
          </Text>
        )}
      </Box>
      <Box
        data-callout-grid-card
        mx={[2, 3]}
        my={[3, 4]}
        sx={{ direction: 'ltr' }}
      >
        <H
          sx={{
            mb: [2, 3],
            variant: 'styles.h2',
            fontFamily: 'headingSecondary',
          }}
        >
          {title}
        </H>
        {typeof description === 'string' ? (
          <Text>{description}</Text>
        ) : (
          <SanityRichText blocks={description} />
        )}

        <Flex mt={[3, 4]}>
          {links &&
            links.length > 0 &&
            links.map(({ url, label }) =>
              url !== '' ? (
                <Link
                  href={url}
                  key={url}
                  sx={{
                    ':first-of-type': {
                      mr: links.length > 1 ? 3 : 0,
                    },
                  }}
                  variant={'underline'}
                >
                  {label}
                </Link>
              ) : (
                <Link
                  href={url}
                  key={url}
                  onClick={(e) => e.preventDefault()}
                  sx={{
                    ':first-of-type': {
                      mr: links.length > 1 ? 3 : 0,
                    },
                  }}
                  variant={'underline'}
                >
                  {label}
                </Link>
              ),
            )}
        </Flex>
      </Box>
    </Box>
  );
};

export const CalloutGridCardFull = ({
  title,
  caption,
  description,
  alt,
  links,
  className,
  sx,
  ...imageProps
}: Omit<CalloutGridCardProps, 'index'>): JSX.Element => (
  <Box
    className={className}
    data-module="CalloutGridCard"
    sx={{
      mb: 4,
      width: '100%',
      ...sx,
    }}
  >
    <Box data-module="CalloutGridCard" sx={{ position: 'relative' }}>
      {imageProps.imageSrc && <Picture alt={alt} src={imageProps.imageSrc} />}
      {imageProps.gatsbyImageData && (
        <IntrinsicImage
          alt={alt}
          image={imageProps.gatsbyImageData}
          ratio={
            imageProps.gatsbyImageData.width / imageProps.gatsbyImageData.height
          }
        />
      )}
      {caption && (
        <Text
          sx={{
            position: 'absolute',
            fontSize: [1, 2],
            bottom: [2, 3],
            right: [2, 3],
            color: 'mediaCaption',
            zIndex: 5,
          }}
        >
          {caption}
        </Text>
      )}
    </Box>
    <Flex
      sx={{
        mt: [3, null, 6],
        mb: [3, null, 4],
        mx: [2, 3],
        flexDirection: ['column', null, 'row'],
      }}
    >
      <H
        sx={{
          variant: 'styles.h2',
          fontSize: [5, null, 7],
          flex: ['none', null, '0 0 50%'],
          mr: 3,
          fontFamily: 'headingSecondary',
          my: [2, 2, 0],
        }}
      >
        {title}
      </H>
      <Box sx={{ flex: ['none', null, '1 1 auto'] }}>
        <Text>{description as string}</Text>
        <Flex mt={3}>
          {links &&
            links.map(({ url, label }) => (
              <Link
                href={url}
                key={url}
                sx={{
                  ':first-of-type': {
                    mr: links.length > 1 ? 3 : 0,
                  },
                }}
                variant={'underline'}
              >
                {label}
              </Link>
            ))}
        </Flex>
      </Box>
    </Flex>
  </Box>
);
export const CalloutGridCardFullWidth = ({
  title,
  description,
  alt,
  links,
  align,
  className,
  sx,
  ...imageProps
}: Omit<CalloutGridCardProps, 'index'>): JSX.Element => {
  return (
    <Box
      className={className}
      sx={{
        width: '100%',
        ...sx,
      }}
    >
      <Grid
        columns={['1fr', '1fr 400px', '1fr 400px 10%']}
        gap={0}
        sx={{
          alignItems: 'center',
          gridAutoFlow: 'dense',
          direction: align === 'right' ? 'rtl' : 'ltr',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
          }}
        >
          {imageProps.imageSrc && (
            <Picture alt={alt} src={imageProps.imageSrc} />
          )}
          {imageProps.gatsbyImageData && (
            <IntrinsicImage
              alt={alt}
              image={imageProps.gatsbyImageData}
              ratio={
                imageProps.gatsbyImageData.width /
                imageProps.gatsbyImageData.height
              }
            />
          )}
        </Box>
        <Box
          sx={{
            padding: '32px',
            pt: ['24px', null, '32px'],
            textAlign: 'left',
          }}
        >
          <H
            sx={{
              mb: [2, 3],
              variant: 'styles.h2',
              fontFamily: 'headingSecondary',
            }}
          >
            {title}
          </H>
          <Text>{description as string}</Text>
          <Flex
            mt={3}
            sx={{
              direction: 'ltr',
            }}
          >
            {links &&
              links.map(({ url, label }) => (
                <Link
                  href={url}
                  key={url}
                  sx={{
                    ':first-of-type': {
                      mr: links.length > 1 ? 3 : 0,
                    },
                  }}
                  variant={'underline'}
                >
                  {label}
                </Link>
              ))}
          </Flex>
        </Box>
      </Grid>
    </Box>
  );
};

export type ImageGridCardProps = Omit<
  CalloutGridCardProps,
  'title' | 'links' | 'index' | 'caption' | 'description'
> & {
  caption?: string;
  sx?: ThemeUIStyleObject;
};
export const ImageGridCard = ({
  caption,
  alt,
  maxWidth = 395,
  className,
  ratio,
  sx,
  ...imageProps
}: ImageGridCardProps): JSX.Element => (
  <Box
    className={className}
    sx={{
      width: ['75%', '100%'],
      maxWidth,
      position: 'relative',
      ...sx,
    }}
  >
    {imageProps.imageSrc && <Picture alt={alt} src={imageProps.imageSrc} />}
    {imageProps.gatsbyImageData && (
      <IntrinsicImage
        alt={alt}
        image={imageProps.gatsbyImageData}
        ratio={ratio}
      />
    )}
    {caption && (
      <Text
        sx={{
          position: 'absolute',
          fontSize: [1, 2],
          bottom: [2, 3],
          right: [2, 3],
          color: 'mediaCaption',
        }}
      >
        {caption}
      </Text>
    )}
  </Box>
);
